import { useState } from "react";
import type { CampaignSummary, TreeCreateResult } from "@/types/campaign";
import { campaignApi } from "@/lib/api";
import { mapTreeCreateResponseFromApi } from "@/lib/mappers";
import type { TreeCreatePayload } from "@/lib/api";

interface Props {
  campaign: CampaignSummary;
  onClose: () => void;
  onCreated: (result: TreeCreateResult) => void;
}

const CONFIDENCE_OPTIONS = [
  { value: "HIGH", label: "Alta — espécie confirmada" },
  { value: "MEDIUM", label: "Média — provável" },
  { value: "LOW", label: "Baixa — incerta" },
  { value: "UNIDENTIFIED", label: "Não identificada" },
];

export default function QuickTreeForm({ campaign, onClose, onCreated }: Props) {
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [ctaCode, setCtaCode] = useState("");
  const [speciesName, setSpeciesName] = useState("");
  const [confidence, setConfidence] = useState("MEDIUM");
  const [fieldNotes, setFieldNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TreeCreateResult | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  const handleGetGPS = () => {
    if (!navigator.geolocation) {
      setError("Geolocalização não suportada neste navegador.");
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude.toFixed(7));
        setLongitude(pos.coords.longitude.toFixed(7));
        setGpsLoading(false);
      },
      (err) => {
        setError(`Erro GPS: ${err.message}`);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleSubmit = async () => {
    setError(null);
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng)) {
      setError("Latitude e longitude são obrigatórios.");
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError("Coordenadas fora do intervalo válido.");
      return;
    }

    setSubmitting(true);
    try {
      const autoCode = ctaCode.trim() || `FIQ-${Date.now().toString(36).toUpperCase()}`;
      const payload: TreeCreatePayload = {
        project_id: campaign.projectId,
        latitude: lat,
        longitude: lng,
        cta_code: autoCode,
        campaign_id: campaign.id,
        registration_confidence: confidence,
        registration_source: "FIELD_QUICK",
      };
      if (speciesName.trim()) payload.species_name = speciesName.trim();
      if (fieldNotes.trim()) payload.field_notes = fieldNotes.trim();

      const raw = await campaignApi.createTree(payload);
      const mapped = mapTreeCreateResponseFromApi(raw);
      setResult(mapped);
      onCreated(mapped);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao registrar árvore.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setLatitude("");
    setLongitude("");
    setCtaCode("");
    setSpeciesName("");
    setConfidence("MEDIUM");
    setFieldNotes("");
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-600 px-5 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold">FIQ-1 — Registro Rápido</h2>
              <p className="text-xs text-emerald-100 mt-0.5">
                {campaign.code} — {campaign.name}
              </p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {result ? (
            <SuccessView result={result} onNewTree={handleReset} onClose={onClose} />
          ) : (
            <div className="space-y-4">
              {/* GPS coordinates */}
              <div>
                <label className="text-xs font-medium text-foreground-600 block mb-1">
                  Coordenadas GPS *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitude"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="Longitude"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={handleGetGPS}
                  disabled={gpsLoading}
                  className="mt-2 w-full py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition flex items-center justify-center gap-1"
                >
                  <i className={gpsLoading ? "ri-loader-4-line animate-spin" : "ri-map-pin-user-line"} />
                  {gpsLoading ? "Obtendo localização..." : "Usar minha localização"}
                </button>
              </div>

              {/* CTA Code (optional) */}
              <div>
                <label className="text-xs font-medium text-foreground-600 block mb-1">
                  Código CTA <span className="text-foreground-400">(opcional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Ex: SBV-ARV-001"
                  value={ctaCode}
                  onChange={(e) => setCtaCode(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              {/* Species (optional) */}
              <div>
                <label className="text-xs font-medium text-foreground-600 block mb-1">
                  Espécie <span className="text-foreground-400">(opcional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Nome popular ou científico"
                  value={speciesName}
                  onChange={(e) => setSpeciesName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              {/* Confidence */}
              <div>
                <label className="text-xs font-medium text-foreground-600 block mb-1">
                  Confiança na identificação
                </label>
                <select
                  value={confidence}
                  onChange={(e) => setConfidence(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                >
                  {CONFIDENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Field notes */}
              <div>
                <label className="text-xs font-medium text-foreground-600 block mb-1">
                  Observações de campo <span className="text-foreground-400">(opcional)</span>
                </label>
                <textarea
                  rows={2}
                  placeholder="Anotações rápidas..."
                  value={fieldNotes}
                  onChange={(e) => setFieldNotes(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> Registrando...
                  </>
                ) : (
                  <>
                    <i className="ri-add-circle-line" /> Registrar árvore
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SuccessView({
  result,
  onNewTree,
  onClose,
}: {
  result: TreeCreateResult;
  onNewTree: () => void;
  onClose: () => void;
}) {
  return (
    <div className="text-center py-4 space-y-4">
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
        <i className={`text-3xl ${result.isNew ? "ri-check-line text-emerald-600" : "ri-alert-line text-yellow-600"}`} />
      </div>
      <div>
        <h3 className="text-base font-semibold text-foreground-900">
          {result.isNew ? "Árvore registrada!" : "Árvore existente detectada"}
        </h3>
        <p className="text-sm text-foreground-500 mt-1">
          {result.isNew
            ? `Código: ${result.ctaCode}`
            : `Ponto próximo da árvore #${result.duplicateOf} (${result.ctaCode})`}
        </p>
      </div>
      <div className="text-xs text-foreground-400 bg-gray-50 rounded-lg p-3 font-mono">
        UUID: {result.uuid}
      </div>
      <div className="flex gap-2">
        <button
          onClick={onNewTree}
          className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition"
        >
          Registrar outra
        </button>
        <button
          onClick={onClose}
          className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-foreground-700 rounded-lg text-sm font-medium transition"
        >
          Fechar
        </button>
      </div>
    </div>
  );
}
